import { useI18n } from '@ecom/i18n';
import { Drawer } from '@ecom/ui-kit';
import { type ReactElement, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { NAV_GROUPS } from './nav';

import { cn } from '@/lib/cn';

/** The navigation links, shared by the desktop sidebar and the mobile menu: one list, never two to keep in sync. */
function NavLinks({ onNavigate }: { onNavigate?: () => void }): ReactElement {
  const { t } = useI18n();
  return (
    <>
      {NAV_GROUPS.map((group) => (
        <div key={group.titleKey} className="mb-5">
          <p className="mb-1.5 px-2 text-xs font-semibold uppercase tracking-wide text-inkFaint">
            {t(group.titleKey)}
          </p>
          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      // 40 px tall on touch screens (a finger), the original compact row with a mouse.
                      'flex min-h-10 items-center justify-between rounded-md px-2.5 py-2 text-sm font-medium text-inkSoft transition-colors hover:bg-surfaceHover hover:text-ink [@media(pointer:fine)]:min-h-0',
                      isActive && 'bg-accentSoft text-accentInk hover:bg-accentSoft',
                    )
                  }
                  end
                >
                  {t(item.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

function Brand(): ReactElement {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-2">
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent font-display text-lg font-bold text-white"
        aria-hidden="true"
      >
        S
      </span>
      <div className="leading-tight">
        <p className="font-display text-base font-bold text-ink">{t('merchant.sidebar.brand')}</p>
        <p className="text-xs text-inkFaint">{t('merchant.sidebar.subtitle')}</p>
      </div>
    </div>
  );
}

/**
 * Desktop sidebar, from the `lg` breakpoint (1024 px). Below it the same links live in `MobileNav`: a 256 px column
 * would leave a phone or a portrait tablet no room for the screen itself.
 */
export function Sidebar(): ReactElement {
  const { t } = useI18n();

  return (
    <aside className="hidden w-64 shrink-0 flex-col border-e border-line bg-surface lg:flex">
      <div className="px-5 py-5">
        <Brand />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4" aria-label={t('merchant.sidebar.navLabel')}>
        <NavLinks />
      </nav>
    </aside>
  );
}

/**
 * The navigation on phones and tablets: the sidebar's own links in a panel opened by the top bar's menu button,
 * closed by a choice, Escape or a tap outside. `footer` carries the top-bar controls that do not fit a narrow bar
 * (language, theme, sign-out) — moved, never removed.
 */
export function MobileNav({
  open,
  onClose,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  footer?: ReactNode;
}): ReactElement {
  const { t } = useI18n();
  return (
    <Drawer
      open={open}
      side="start"
      title={t('merchant.sidebar.brand')}
      onClose={onClose}
      footer={footer}
    >
      <nav aria-label={t('merchant.sidebar.navLabel')} className="-mx-2">
        <NavLinks onNavigate={onClose} />
      </nav>
    </Drawer>
  );
}
